import { useEffect, useRef, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';

import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  GALLERY_UPLOAD_ACCEPT,
  runGalleryUploads,
  summarizeUploads,
  uploadGalleryFileRequest,
  type GalleryUploadEntry,
} from 'gallery';

import { useGalleryText } from './gallery-ui';

interface GalleryUploadZoneProps {
  galleryUuid: string;
  // The picked section, else the API's first one.
  sectionUuid: string | null;
  token?: string;
  onUploaded: () => void;
  onFinished: () => Promise<void> | void;
}

function hasFiles(event: React.DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

export function GalleryUploadZone({
  galleryUuid,
  sectionUuid,
  token,
  onUploaded,
  onFinished,
}: GalleryUploadZoneProps) {
  const { t, plural } = useGalleryText();
  const input = useRef<HTMLInputElement | null>(null);
  const isMounted = useRef(true);
  const [isOver, setIsOver] = useState(false);
  // One list per drop, so a second drop while the first still uploads keeps both.
  const [batches, setBatches] = useState<Record<string, GalleryUploadEntry[]>>({});

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const start = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (!files.length) {
      return;
    }
    const batchId = `${Date.now()}-${Math.random()}`;
    void runGalleryUploads(files, {
      upload: (file, onProgress) =>
        uploadGalleryFileRequest({ galleryUuid, sectionUuid, file, onProgress }, token),
      onChange: (entries) => {
        if (isMounted.current) {
          setBatches((current) => ({ ...current, [batchId]: entries }));
        }
      },
      onUploaded,
    }).then(() => onFinished());
  };

  const entries = Object.values(batches).flat();
  const summary = summarizeUploads(entries);
  const shown = entries.filter((entry) => entry.status !== 'done');

  return (
    <SoftBox>
      <SoftBox
        role="button"
        tabIndex={0}
        onClick={() => input.current?.click()}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            input.current?.click();
          }
        }}
        onDragOver={(event: React.DragEvent) => {
          if (hasFiles(event)) {
            event.preventDefault();
            setIsOver(true);
          }
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(event: React.DragEvent) => {
          if (hasFiles(event)) {
            event.preventDefault();
            setIsOver(false);
            start(event.dataTransfer.files);
          }
        }}
        data-testid="gallery-dropzone"
        sx={{
          border: '2px dashed',
          borderColor: isOver ? 'info.main' : 'grey.400',
          bgcolor: isOver ? 'action.hover' : 'transparent',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          cursor: 'pointer',
        }}
      >
        <SoftTypography variant="button" fontWeight="bold" display="block">
          {t.files.dropTitle}
        </SoftTypography>
        <SoftTypography variant="caption" color="text">
          {t.files.dropHint}
        </SoftTypography>
        <input
          ref={input}
          type="file"
          multiple
          accept={GALLERY_UPLOAD_ACCEPT}
          hidden
          data-testid="gallery-file-input"
          onChange={(event) => {
            start(event.target.files);
            event.target.value = '';
          }}
        />
      </SoftBox>
      {summary.total > 0 ? (
        <SoftBox mt={1} data-testid="gallery-upload-progress">
          <SoftBox display="flex" alignItems="center" gap={1}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
              {interpolateTemplate(summary.active ? t.files.uploading : t.files.uploaded, {
                done: String(summary.done),
                total: String(summary.total - summary.failed),
              })}
              {summary.failed ? ` · ${plural(t.files.notAccepted, summary.failed)}` : ''}
            </SoftTypography>
            {summary.active === 0 ? (
              <SoftButton size="small" variant="text" color="info" onClick={() => setBatches({})}>
                {t.files.clearUploads}
              </SoftButton>
            ) : null}
          </SoftBox>
          {summary.active ? (
            <LinearProgress variant="determinate" value={summary.progress * 100} sx={{ my: 0.5 }} />
          ) : null}
          <SoftBox sx={{ maxHeight: 180, overflow: 'auto' }}>
            {shown.map((entry) => (
              <SoftBox
                key={entry.id}
                display="flex"
                alignItems="center"
                gap={1}
                data-testid="gallery-upload-row"
              >
                <SoftTypography
                  variant="caption"
                  sx={{
                    width: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.fileName}
                </SoftTypography>
                {entry.error ? (
                  <SoftTypography variant="caption" color="error">
                    {t.uploadErrors[entry.error]}
                  </SoftTypography>
                ) : (
                  <LinearProgress
                    variant="determinate"
                    value={entry.progress * 100}
                    sx={{ flex: 1 }}
                  />
                )}
              </SoftBox>
            ))}
          </SoftBox>
        </SoftBox>
      ) : null}
    </SoftBox>
  );
}
